import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, UserPlus, UserX } from "lucide-react";
import { CopyAccessButton } from "@/components/CopyAccessButton";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const RevokeButton = ({ u, onConfirm }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <Button size="sm" variant="ghost" className="gap-1 text-destructive h-7 px-2" data-testid={`staff-revoke-${u.email}`}><UserX className="w-3.5 h-3.5" /> Retirer</Button>
    </AlertDialogTrigger>
    <AlertDialogContent data-testid={`staff-revoke-dialog-${u.email}`}>
      <AlertDialogHeader>
        <AlertDialogTitle>Retirer l'accès de {u.name} ?</AlertDialogTitle>
        <AlertDialogDescription>{u.email} ne pourra plus se connecter (sessions en cours fermées). Ses devis sont conservés. Vous pourrez lui redonner un accès plus tard avec le formulaire ci-dessus.</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid={`staff-revoke-cancel-${u.email}`}>Annuler</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid={`staff-revoke-confirm-${u.email}`}>Retirer l'accès</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

const KINDS = [["manager", "Manager"], ["admin", "Administrateur"]];
const fmt = (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : "");

export const StaffDialog = ({ open, onOpenChange, onDone }) => {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", kind: "manager" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/staff").then(({ data }) => setList(data)).catch(() => {});
  useEffect(() => { if (open) { setForm({ name: "", email: "", kind: "manager" }); load(); } }, [open]);
  const revoke = async (u) => {
    try {
      await api.post(`/admin/staff/${u.id}/revoke`);
      toast.success(`Accès retiré : ${u.email}`);
      load(); onDone?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/staff", form);
      toast.success(`${data.created ? "Compte créé" : "Accès mis à jour"} : ${data.email}`);
      if (data.invited) toast.success("Email envoyé avec le lien pour choisir son mot de passe (7 jours)");
      else toast.error("L'email n'a pas pu être envoyé : utilisez « Mot de passe oublié » sur la page de connexion.");
      setForm({ name: "", email: "", kind: form.kind });
      load(); onDone?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1.5rem)] max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="staff-dialog">
        <DialogHeader><DialogTitle>Comptes internes — administrateurs et managers</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3 border rounded-md p-3 bg-muted/30 min-w-0">
          <div><Label className="text-xs">Nom</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="NOM Prénom" data-testid="staff-name" /></div>
          <div><Label className="text-xs">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required placeholder="prenom.nom@fautras.com" data-testid="staff-email" /></div>
          <div className="col-span-full flex flex-wrap gap-2" data-testid="staff-kind">
            {KINDS.map(([k, l]) => (
              <button key={k} type="button" onClick={() => setForm({ ...form, kind: k })} data-testid={`staff-kind-${k}`}
                className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${form.kind === k ? "bg-primary text-primary-foreground border-primary" : "hover:bg-muted"}`}>{l}</button>
            ))}
          </div>
          <p className="col-span-full text-xs text-muted-foreground">
            {form.kind === "admin" ? "Accès complet à l'Admin (modèles, prix, revendeurs, promotions…)." : "Voit tous les devis des vendeurs France et Suisse et peut accorder une remise revendeur, sans accès à l'Admin."}
            {" "}La personne reçoit un email avec un lien personnel (7 jours) pour choisir son mot de passe. Si le compte existe déjà, son accès est modifié.
          </p>
          <div className="col-span-full flex justify-end">
            <Button type="submit" disabled={busy} className="gap-2 w-full sm:w-auto whitespace-normal h-auto py-2" data-testid="staff-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Créer l'accès et envoyer l'email
            </Button>
          </div>
        </form>
        <div className="divide-y divide-border border rounded-md" data-testid="staff-list">
          {list.map((u) => (
            <div key={u.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm" data-testid={`staff-row-${u.email}`}>
              <span className="flex-1 min-w-0 break-words"><b>{u.name}</b> · {u.email}</span>
              <span className={`text-[11px] px-2 py-0.5 rounded-full border ${u.kind === "admin" ? "border-primary text-primary" : ""}`}>{u.kind === "admin" ? "Admin" : "Manager"}</span>
              <span className="text-[11px] text-muted-foreground sm:w-40 sm:text-right">
                {u.last_login_at ? `connecté le ${fmt(u.last_login_at)}` : u.invited_at ? `invité le ${fmt(u.invited_at)}` : "jamais connecté"}
              </span>
              {!u.protected && <CopyAccessButton r={u} onCopied={load} />}
              {!u.protected && <RevokeButton u={u} onConfirm={() => revoke(u)} />}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
